import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const benchApi = {
  /**
   * Get bench assignment data for an employee project.
   * @param {string} employeeProjectId
   * @returns {Promise<Object>} Bench data (type, star, comments, and optionally salary)
   */
  getBenchData: async (employeeProjectId) => {
    try {
      return await authenticatedApi.get(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/bench-data`
      );
    } catch (error) {
      if (error.status === 404) {
        return null;
      }
      throw new ApiError('Failed to fetch bench data', error.status || 500);
    }
  },

  /**
   * Create bench assignment data for an employee project.
   * @param {string} employeeProjectId
   * @param {Object} data - { benchType, flagAsStar, salary, salaryCurrency, salaryPeriod, comments }
   * @returns {Promise<Object>}
   */
  createBenchData: async (employeeProjectId, data) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/bench-data`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid bench data', 400);
      }
      throw new ApiError('Failed to create bench data', error.status || 500);
    }
  },

  /**
   * Update bench assignment data for an employee project.
   * @param {string} employeeProjectId
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>}
   */
  updateBenchData: async (employeeProjectId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/bench-data`,
        data
      );
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Bench data not found', 404);
      }
      throw new ApiError('Failed to update bench data', error.status || 500);
    }
  },

  /**
   * Delete bench assignment data for an employee project.
   * @param {string} employeeProjectId
   * @returns {Promise<void>}
   */
  deleteBenchData: async (employeeProjectId) => {
    try {
      return await authenticatedApi.delete(
        `${API_BASE_URL}/employee-projects/${employeeProjectId}/bench-data`
      );
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Bench data not found', 404);
      }
      throw new ApiError('Failed to delete bench data', error.status || 500);
    }
  },

  /**
   * Get bench report with filtering and pagination.
   * @param {Object} params - Query parameters
   * @param {string} [params.businessUnitId]
   * @param {string} [params.clusterId]
   * @param {boolean} [params.includeSubUnits]
   * @param {string} [params.benchType] - BENCH or CHANGE
   * @param {boolean} [params.flagAsStar]
   * @param {string} [params.search]
   * @param {string} [params.orderDirection] - asc or desc
   * @param {number} [params.limit]
   * @param {string} [params.cursor]
   * @returns {Promise<Object>} { summary, employees, pageInfo }
   */
  getBenchReport: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.businessUnitId) {
        searchParams.set('businessUnitId', params.businessUnitId);
      }
      if (params.buScope) {
        searchParams.set('buScope', params.buScope);
      }
      if (params.clusterId) {
        searchParams.set('clusterId', params.clusterId);
      }
      if (params.includeSubUnits !== undefined) {
        searchParams.set('includeSubUnits', String(params.includeSubUnits));
      }
      if (params.benchType) {
        searchParams.set('benchType', params.benchType);
      }
      if (params.flagAsStar !== undefined) {
        searchParams.set('flagAsStar', String(params.flagAsStar));
      }
      if (params.search) {
        searchParams.set('search', params.search);
      }
      if (params.employeeId) {
        searchParams.set('employeeId', params.employeeId);
      }
      if (params.leafSegmentIds && params.leafSegmentIds.length > 0) {
        searchParams.set('leafSegmentIds', params.leafSegmentIds.join(','));
      }
      if (params.positionIds && params.positionIds.length > 0) {
        searchParams.set('positionIds', params.positionIds.join(','));
      }
      if (params.orderDirection) {
        searchParams.set('orderDirection', params.orderDirection);
      }
      if (params.limit) {
        searchParams.set('limit', String(params.limit));
      }
      if (params.cursor) {
        searchParams.set('cursor', params.cursor);
      }
      if (params.date) {
        searchParams.set('date', params.date);
      }

      const queryString = searchParams.toString();
      const url = `${API_BASE_URL}/reports/bench${queryString ? `?${queryString}` : ''}`;

      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch bench report', error.status || 500);
    }
  },
  /**
   * ADR-108: filter options (facets) for the bench reports.
   * Only the Cluster narrows the options; other report filters are not sent.
   * @param {Object} params
   * @param {string} params.report - BENCH_REPORT_KEYS value (bench | benchEntryLag)
   * @param {string[]} [params.facets] - defaults to ['businessUnits'] server-side
   * @param {string} [params.clusterId]
   * @param {boolean} [params.includeSubUnits]
   * @param {string} [params.selectedBusinessUnitId]
   * @param {string} [params.businessUnitId] - ADR-110: organisational scope of the positions facet
   * @param {string} [params.buScope] - ADR-110: 'direct' | 'hierarchy'
   * @param {string[]} [params.selectedPositionIds] - ADR-110
   * @returns {Promise<Object>} { report, asOfDate, businessUnits?: { countMode, total, withoutBusinessUnitCount, roots, selected? },
   *   positions?: { countMode, total, withoutPositionCount, outsideAllowedTypesCount, groups, selected? } }
   */
  getBenchReportFacets: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set('report', params.report);

      if (params.facets && params.facets.length > 0) {
        searchParams.set('facets', params.facets.join(','));
      }
      if (params.clusterId) {
        searchParams.set('clusterId', params.clusterId);
      }
      if (params.includeSubUnits !== undefined) {
        searchParams.set('includeSubUnits', String(params.includeSubUnits));
      }
      if (params.selectedBusinessUnitId) {
        searchParams.set('selectedBusinessUnitId', params.selectedBusinessUnitId);
      }
      if (params.businessUnitId) {
        searchParams.set('businessUnitId', params.businessUnitId);
      }
      if (params.buScope) {
        searchParams.set('buScope', params.buScope);
      }
      if (params.selectedPositionIds && params.selectedPositionIds.length > 0) {
        searchParams.set('selectedPositionIds', params.selectedPositionIds.join(','));
      }

      return await authenticatedApi.get(`${API_BASE_URL}/reports/bench/facets?${searchParams.toString()}`);
    } catch (error) {
      throw new ApiError('Failed to fetch bench report filter options', error.status || 500);
    }
  },

  /**
   * Get bench entry lag report with filtering and pagination.
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} { summary, employees, pageInfo }
   */
  getBenchEntryLagReport: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.businessUnitId) {
        searchParams.set('businessUnitId', params.businessUnitId);
      }
      if (params.buScope) {
        searchParams.set('buScope', params.buScope);
      }
      if (params.clusterId) {
        searchParams.set('clusterId', params.clusterId);
      }
      if (params.includeSubUnits !== undefined) {
        searchParams.set('includeSubUnits', String(params.includeSubUnits));
      }
      if (params.benchType) {
        searchParams.set('benchType', params.benchType);
      }
      if (params.lagRange) {
        searchParams.set('lagRange', params.lagRange);
      }
      if (params.employeeId) {
        searchParams.set('employeeId', params.employeeId);
      }
      if (params.orderBy) {
        searchParams.set('orderBy', params.orderBy);
      }
      if (params.orderDirection) {
        searchParams.set('orderDirection', params.orderDirection);
      }
      if (params.limit) {
        searchParams.set('limit', String(params.limit));
      }
      if (params.cursor) {
        searchParams.set('cursor', params.cursor);
      }

      const queryString = searchParams.toString();
      const url = `${API_BASE_URL}/reports/bench-entry-lag${queryString ? `?${queryString}` : ''}`;

      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch bench entry lag report', error.status || 500);
    }
  },
};
